import axios, { AxiosResponse } from 'axios';
import { AuthResponse, RegisterResponse, LoginData, RecoveryData, User } from '../types/auth';
import { Chat, Message, SystemChat } from '../types/chat';
import { UserProfile } from '../types/user';
import { storage } from '../utils/storage';

export interface GroupChat {
  id: number;
  name?: string | null;
  description?: string;
  creatorId?: number;
  inviteCode: string;
  groupCode: string;
  members: User[];
  messages: Message[];
  createdAt: string;
  lastMessage?: Message;
  isActive?: boolean;
  maxMembers?: number;
  creator?: User;
  unreadCount: number;
  isNew?: boolean;
  isGroupChat?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<AxiosResponse<{ access_token: string; refresh_token: string }>> | null = null;

api.interceptors.request.use((config) => {
  const token = storage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = storage.getRefreshToken();

      if (refreshToken) {
        try {
          if (refreshPromise) {
            console.log('Ожидание существующего запроса на обновление токенов');
            const { data } = await refreshPromise;
            storage.setToken(data.access_token);
            storage.setRefreshToken(data.refresh_token);
            originalRequest.headers['Authorization'] = `Bearer ${data.access_token}`;
            return api(originalRequest);
          }

          console.log('Отправка запроса на /auth/refresh с refresh_token:', refreshToken);
          refreshPromise = authAPI.refreshToken();
          const { data } = await refreshPromise;

          storage.setToken(data.access_token);
          storage.setRefreshToken(data.refresh_token);
          console.log('Токены успешно обновлены:', data);

          refreshPromise = null;

          originalRequest.headers['Authorization'] = `Bearer ${data.access_token}`;
          return api(originalRequest);
        } catch (err: any) {
          console.error('Ошибка при обновлении токенов:', err);
          refreshPromise = null;

          if (err.response?.status === 401 || err.response?.status === 403) {
            console.log('Refresh token недействителен, очищаем storage и перенаправляем на /login');
            storage.clear();
            storage.clearRegistrationData();
            storage.removeRefreshToken();
            window.location.href = '/login';
            return Promise.reject(err);
          }

          return Promise.reject(err);
        }
      } else {
        console.log('Refresh token отсутствует, перенаправляем на /login');
        storage.clear();
        storage.clearRegistrationData();
        storage.removeRefreshToken();
        window.location.href = '/login';
        return Promise.reject(error); 
      }
    }
    return Promise.reject(error);
  },
);

// API методы для аутентификации
export const authAPI = {
  register: (): Promise<AxiosResponse<RegisterResponse>> => api.post('/auth/register'),

  login: (data: LoginData): Promise<AxiosResponse<AuthResponse>> => api.post('/auth/login', data),

  recover: (data: RecoveryData): Promise<AxiosResponse<AuthResponse>> => api.post('/auth/recover', data),

  getMe: (): Promise<AxiosResponse<User>> => api.get('/auth/me'),

  refreshToken: (refreshToken?: string): Promise<AxiosResponse<{ access_token: string; refresh_token: string }>> =>
    api.post('/auth/refresh', {
      refresh_token: refreshToken || storage.getRefreshToken(),
    }),
};

// API методы для системного чата
export const systemChatAPI = {
  getSystemChat: (): Promise<AxiosResponse<SystemChat>> => api.get('/system-chat'),

  sendSystemMessage: (content: string): Promise<AxiosResponse<Message>> =>
    api.post('/system-chat/messages', { content }),
};

// API методы для чатов
export const chatAPI = {
  getMyChats: (): Promise<AxiosResponse<Chat[]>> => api.get('/chats/my-chats'),

  getChatWithMessages: (chatId: number): Promise<AxiosResponse<Chat>> => api.get(`/chats/${chatId}`),

  sendMessage: (chatId: number, content: string): Promise<AxiosResponse<Message>> =>
    api.post(`/chats/${chatId}/messages`, { content }),

  deleteChat: (chatId: number): Promise<AxiosResponse<void>> => api.delete(`/chats/${chatId}`),

  clearChat: (chatId: number): Promise<AxiosResponse<void>> => api.post(`/chats/${chatId}/clear`),

  findRandomUser: (): Promise<AxiosResponse<UserProfile>> => api.get('/chats/find-random-user'),

  findChatByUser: (targetUserNumber: string): Promise<AxiosResponse<Chat>> =>
    api.post('/chats/find-by-user', { targetUserNumber }),

  getSystemChat: (): Promise<AxiosResponse<SystemChat>> => api.get('/system-chat'),
};

// API методы для групповых чатов
export const groupChatAPI = {
  getMyGroups: (): Promise<AxiosResponse<GroupChat[]>> => api.get('/group-chats'),

  getGroupWithMessages: (groupId: number): Promise<AxiosResponse<GroupChat>> => api.get(`/group-chats/${groupId}`),

  joinGroup: (inviteCode: string): Promise<AxiosResponse<GroupChat>> => api.post('/group-chats/join', { inviteCode }),

  sendGroupMessage: (groupId: number, content: string): Promise<AxiosResponse<Message>> =>
    api.post(`/group-chats/${groupId}/messages`, { content }),

  findRandomUserInGroup: (groupId: number): Promise<AxiosResponse<{ chatId: number; companionNumber: string }>> =>
    api.post(`/group-chats/${groupId}/random-chat`),

  leaveGroup: (groupId: number): Promise<AxiosResponse<void>> => api.post(`/group-chats/${groupId}/leave`),

  createGroup: (name: string | null, maxMembers: number): Promise<AxiosResponse<GroupChat>> =>
    api.post('/group-chats', { name, maxMembers }),

  updateGroupName: (groupId: number, name: string | null): Promise<AxiosResponse<GroupChat>> =>
    api.patch(`/group-chats/${groupId}/name`, { name }),

  getInviteCode: (groupId: number): Promise<AxiosResponse<{ inviteCode: string }>> =>
    api.get(`/group-chats/${groupId}/qr-code`),
};

export default api;
